import React, { useEffect, useState } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Loader2,
  GripVertical,
  X,
  Check
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const iconOptions = ['Home', 'Truck', 'Sparkles', 'Square', 'Building2', 'Stairs'];

// Helper function to generate URL-friendly slugs
const generateSlug = (text) => {
  if (!text) return '';
  // Convert to lowercase
  let slug = text.toLowerCase();
  // Replace Swedish characters
  const replacements = {'å': 'a', 'ä': 'a', 'ö': 'o', 'é': 'e', 'è': 'e'};
  for (const [old, newChar] of Object.entries(replacements)) {
    slug = slug.replace(new RegExp(old, 'g'), newChar);
  }
  // Replace spaces and special chars with hyphens
  slug = slug.replace(/[^a-z0-9]+/g, '-');
  // Remove leading/trailing hyphens
  slug = slug.replace(/^-+|-+$/g, '');
  // Remove duplicate hyphens
  slug = slug.replace(/-+/g, '-');
  return slug;
};

const defaultFeatures = [
  { text_sv: "All utrustning och rengöringsmedel ingår", text_en: "All equipment and cleaning supplies included" },
  { text_sv: "Försäkrad och ID-kontrollerad personal", text_en: "Insured and ID-verified staff" },
  { text_sv: "Nöjd-kund-garanti", text_en: "Satisfaction guarantee" },
  { text_sv: "RUT-avdrag (50% skattereduktion)", text_en: "RUT deduction (50% tax reduction)" },
  { text_sv: "Flexibla bokningsalternativ", text_en: "Flexible booking options" },
  { text_sv: "Miljövänliga produkter", text_en: "Eco-friendly products" }
];

const AdminServices = () => {
  const { api } = useAdmin();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);

  const emptyService = {
    title_sv: '',
    title_en: '',
    slug: '',
    description_sv: '',
    description_en: '',
    full_description_sv: '',
    full_description_en: '',
    icon: 'Sparkles',
    base_price: 0,
    price_unit_sv: 'per tillfälle',
    price_unit_en: 'per session',
    active: true,
    order: 0,
    features: [...defaultFeatures]
  };

  const [formData, setFormData] = useState(emptyService);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const response = await api.getServices();
      setServices(response.data);
    } catch (error) {
      console.error('Failed to load services:', error);
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingService(null);
    setFormData({ ...emptyService, order: services.length });
    setDialogOpen(true);
  };

  const handleEdit = (service) => {
    setEditingService(service);
    // Ensure features and slug exist when editing
    setFormData({
      ...service,
      slug: service.slug || generateSlug(service.title_en),
      features: service.features || [...defaultFeatures]
    });
    setDialogOpen(true);
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    
    try {
      await api.deleteService(serviceId);
      setServices(services.filter(s => s.id !== serviceId));
      toast.success('Service deleted');
    } catch (error) {
      console.error('Failed to delete service:', error);
      toast.error('Failed to delete service');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingService) {
        const response = await api.updateService(editingService.id, formData);
        setServices(services.map(s => s.id === editingService.id ? response.data : s));
        toast.success('Service updated');
      } else {
        const response = await api.createService(formData);
        setServices([...services, response.data]);
        toast.success('Service created');
      }
      setDialogOpen(false);
    } catch (error) {
      console.error('Failed to save service:', error);
      toast.error('Failed to save service');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-purple-600" size={40} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Services</h1>
          <p className="text-gray-600">Manage your cleaning services</p>
        </div>
        <Button onClick={handleCreate} className="bg-purple-600 hover:bg-purple-700">
          <Plus size={20} className="mr-2" />
          Add Service
        </Button>
      </div>

      {/* Services List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        {services.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No services yet. Create your first service!
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {services.map((service) => (
              <div key={service.id} className="p-4 flex items-center gap-4 hover:bg-gray-50">
                <GripVertical className="text-gray-300 cursor-grab" size={20} />
                
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-purple-600 text-xl">
                    {service.icon === 'Home' && '🏠'}
                    {service.icon === 'Truck' && '🚚'}
                    {service.icon === 'Sparkles' && '✨'}
                    {service.icon === 'Square' && '🪟'}
                    {service.icon === 'Building2' && '🏢'}
                    {service.icon === 'Stairs' && '🪜'}
                  </span>
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-gray-900">{service.title_sv}</h3>
                    {!service.active && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">
                        Inactive
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 truncate">{service.description_sv}</p>
                </div>
                
                <div className="text-right">
                  <p className="font-semibold text-purple-600">{service.base_price} kr</p>
                  <p className="text-xs text-gray-500">{service.price_unit_sv}</p>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(service)}
                  >
                    <Pencil size={16} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(service.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Service Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingService ? 'Edit Service' : 'Create Service'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Active Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label className="font-medium">Active</Label>
                <p className="text-sm text-gray-500">Show this service on the website</p>
              </div>
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) => handleChange('active', checked)}
              />
            </div>

            {/* Icon Selection */}
            <div>
              <Label className="mb-2 block">Icon</Label>
              <div className="flex gap-2 flex-wrap">
                {iconOptions.map((icon) => (
                  <button
                    key={icon}
                    type="button"
                    onClick={() => handleChange('icon', icon)}
                    className={`w-12 h-12 rounded-lg border-2 flex items-center justify-center text-xl transition-all ${
                      formData.icon === icon
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-200'
                    }`}
                  >
                    {icon === 'Home' && '🏠'}
                    {icon === 'Truck' && '🚚'}
                    {icon === 'Sparkles' && '✨'}
                    {icon === 'Square' && '🪟'}
                    {icon === 'Building2' && '🏢'}
                    {icon === 'Stairs' && '🪜'}
                  </button>
                ))}
              </div>
            </div>

            {/* Titles */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="title_sv">Title (Swedish)</Label>
                <Input
                  id="title_sv"
                  value={formData.title_sv}
                  onChange={(e) => handleChange('title_sv', e.target.value)}
                  placeholder="Hemstädning"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="title_en">Title (English)</Label>
                <Input
                  id="title_en"
                  value={formData.title_en}
                  onChange={(e) => {
                    handleChange('title_en', e.target.value);
                    // Auto-generate slug if empty or matches old auto-generated slug
                    if (!formData.slug || formData.slug === generateSlug(formData.title_en)) {
                      handleChange('slug', generateSlug(e.target.value));
                    }
                  }}
                  placeholder="Home Cleaning"
                  className="mt-1"
                />
              </div>
            </div>

            {/* URL Slug */}
            <div>
              <Label htmlFor="slug">URL Slug</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => handleChange('slug', generateSlug(e.target.value))}
                  placeholder="home-cleaning"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleChange('slug', generateSlug(formData.title_en))}
                  className="shrink-0"
                >
                  Auto-generate
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                URL: stockholmelite.se/services/<span className="font-medium">{formData.slug || 'slug'}</span>
              </p>
            </div>

            {/* Descriptions */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="description_sv">Short Description (Swedish)</Label>
                <Textarea
                  id="description_sv"
                  value={formData.description_sv}
                  onChange={(e) => handleChange('description_sv', e.target.value)}
                  placeholder="Kort beskrivning på svenska..."
                  className="mt-1"
                  rows={3}
                />
                <p className="text-xs text-gray-500 mt-1">Shown on service cards</p>
              </div>
              <div>
                <Label htmlFor="description_en">Short Description (English)</Label>
                <Textarea
                  id="description_en"
                  value={formData.description_en}
                  onChange={(e) => handleChange('description_en', e.target.value)}
                  placeholder="Short description in English..."
                  className="mt-1"
                  rows={3}
                />
                <p className="text-xs text-gray-500 mt-1">Shown on service cards</p>
              </div>
            </div>

            {/* Full Descriptions */}
            <div className="space-y-4">
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-900 mb-2">Full Description (Service Detail Page)</h4>
                <p className="text-sm text-gray-500 mb-4">This detailed description appears when users click "Learn more" on a service.</p>
              </div>
              <div>
                <Label htmlFor="full_description_sv">Full Description (Swedish)</Label>
                <Textarea
                  id="full_description_sv"
                  value={formData.full_description_sv}
                  onChange={(e) => handleChange('full_description_sv', e.target.value)}
                  placeholder="Fullständig beskrivning på svenska. Skriv allt om tjänsten här..."
                  className="mt-1"
                  rows={6}
                />
              </div>
              <div>
                <Label htmlFor="full_description_en">Full Description (English)</Label>
                <Textarea
                  id="full_description_en"
                  value={formData.full_description_en}
                  onChange={(e) => handleChange('full_description_en', e.target.value)}
                  placeholder="Full description in English. Write everything about this service here..."
                  className="mt-1"
                  rows={6}
                />
              </div>
            </div>

            {/* What's Included Features */}
            <div className="space-y-4">
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-900 mb-2">What's Included Features</h4>
                <p className="text-sm text-gray-500 mb-4">These features appear as checkmarks on the service detail page.</p>
              </div>
              
              {(formData.features || defaultFeatures).map((feature, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Feature {index + 1}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newFeatures = [...(formData.features || defaultFeatures)];
                        newFeatures.splice(index, 1);
                        handleChange('features', newFeatures);
                      }}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                    >
                      <X size={16} />
                    </Button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Swedish</Label>
                      <Input
                        value={feature.text_sv}
                        onChange={(e) => {
                          const newFeatures = [...(formData.features || defaultFeatures)];
                          newFeatures[index] = { ...newFeatures[index], text_sv: e.target.value };
                          handleChange('features', newFeatures);
                        }}
                        placeholder="Text på svenska"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">English</Label>
                      <Input
                        value={feature.text_en}
                        onChange={(e) => {
                          const newFeatures = [...(formData.features || defaultFeatures)];
                          newFeatures[index] = { ...newFeatures[index], text_en: e.target.value };
                          handleChange('features', newFeatures);
                        }}
                        placeholder="Text in English"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const newFeatures = [...(formData.features || defaultFeatures), { text_sv: '', text_en: '' }];
                  handleChange('features', newFeatures);
                }}
                className="w-full"
              >
                <Plus size={16} className="mr-2" />
                Add Feature
              </Button>
            </div>

            {/* Price */}
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="base_price">Base Price (kr)</Label>
                <Input
                  id="base_price"
                  type="number"
                  value={formData.base_price}
                  onChange={(e) => handleChange('base_price', parseFloat(e.target.value) || 0)}
                  placeholder="349"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="price_unit_sv">Price Unit (Swedish)</Label>
                <Input
                  id="price_unit_sv"
                  value={formData.price_unit_sv}
                  onChange={(e) => handleChange('price_unit_sv', e.target.value)}
                  placeholder="per tillfälle"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="price_unit_en">Price Unit (English)</Label>
                <Input
                  id="price_unit_en"
                  value={formData.price_unit_en}
                  onChange={(e) => handleChange('price_unit_en', e.target.value)}
                  placeholder="per session"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Order */}
            <div>
              <Label htmlFor="order">Display Order</Label>
              <Input
                id="order"
                type="number"
                value={formData.order}
                onChange={(e) => handleChange('order', parseInt(e.target.value) || 0)}
                placeholder="0"
                className="mt-1 w-24"
              />
              <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {saving ? (
                <Loader2 className="animate-spin mr-2" size={18} />
              ) : (
                <Check className="mr-2" size={18} />
              )}
              {editingService ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminServices;
